import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Colors } from '../theme';

type Props = {
  /** Only the authoritative Vibe Verified status should ever be passed here
   * (TonyteUser.isVibeVerified or PublicUserProfile.faceVerified — both are
   * already `data.isVibeVerified === true || data.faceVerified === true`
   * normalized at the data layer). Never infer this from pending/attempted
   * state. */
  verified?: boolean;
  /** The AVATAR's size (diameter), not the badge's own size — the badge
   * scales itself off this and clamps to stay legible at tiny sizes without
   * ever getting oversized on large profile photos. */
  size: number;
  style?: StyleProp<ViewStyle>;
};

// Above this avatar size, the badge gets a single one-shot pop-in — never a
// looping animation — reserved for large profile photos (own/public profile
// hero avatars). List rows (chat, members, cards) stay fully static.
const LARGE_AVATAR_THRESHOLD = 88;

/**
 * The one canonical Vibe Verified badge — orange, glowing, shiny, a real
 * check icon (never emoji, never text). Self-positions bottom-right via
 * absolute position, so callers just render it as a sibling of their
 * existing avatar Image inside a `position: relative` (RN's View default)
 * container that does NOT clip with `overflow: hidden` at that level —
 * see ChatAvatar.tsx for the established outer-wrapper/inner-clip pattern
 * this follows.
 */
const VibeVerifiedBadge: React.FC<Props> = ({ verified, size, style }) => {
  const scale = useRef(new Animated.Value(0.7)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const isLarge = size >= LARGE_AVATAR_THRESHOLD;

  useEffect(() => {
    if (!verified) return;
    if (!isLarge) {
      // List/card context — render instantly, no animation cost per row.
      scale.setValue(1);
      opacity.setValue(1);
      return;
    }
    scale.setValue(0.7);
    opacity.setValue(0);
    Animated.parallel([
      Animated.spring(scale, { toValue: 1, friction: 5, tension: 60, useNativeDriver: true }),
      Animated.timing(opacity, { toValue: 1, duration: 260, useNativeDriver: true }),
    ]).start();
  }, [verified, isLarge, scale, opacity]);

  if (!verified) return null;

  const badgeSize = Math.max(14, Math.min(size * 0.32, 30));
  const iconSize = badgeSize * 0.54;
  const ringWidth = Math.max(1.2, badgeSize * 0.09);
  const glowSize = badgeSize * 1.9;

  return (
    <Animated.View
      pointerEvents="none"
      importantForAccessibility="no-hide-descendants"
      style={[
        styles.wrap,
        { width: badgeSize, height: badgeSize, opacity, transform: [{ scale }] },
        style,
      ]}
    >
      {/* Soft outer glow — layered translucent circles, no BlurView, no
          animation: cheap enough for repeated list rows. */}
      <View
        style={[
          styles.glow,
          {
            width: glowSize,
            height: glowSize,
            borderRadius: glowSize / 2,
            left: (badgeSize - glowSize) / 2,
            top: (badgeSize - glowSize) / 2,
          },
        ]}
      />
      <View
        style={[
          styles.core,
          {
            width: badgeSize,
            height: badgeSize,
            borderRadius: badgeSize / 2,
            borderWidth: ringWidth,
          },
        ]}
      >
        {/* Glossy highlight — static, gives the "slightly shiny" premium look. */}
        <View
          style={[
            styles.shine,
            {
              width: badgeSize * 0.5,
              height: badgeSize * 0.3,
              borderRadius: badgeSize * 0.3,
              top: badgeSize * 0.1,
              left: badgeSize * 0.15,
            },
          ]}
        />
        <Icon name="check" size={iconSize} color="#2a1200" />
      </View>
    </Animated.View>
  );
};

export default VibeVerifiedBadge;

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  glow: {
    position: 'absolute',
    backgroundColor: 'rgba(242,125,38,0.38)',
  },
  core: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.sunset,
    borderColor: '#fff',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.9,
    shadowRadius: 4,
    elevation: 3,
  },
  shine: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,0.55)',
  },
});
